import { Button, ButtonProps } from '@chakra-ui/react'
import { ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Icon } from '../Icon'

/**
 * Aparência única do botão sobre o banner escuro. Era este bloco de sete props, copiado onze
 * vezes entre `ContestDetailPage` e `EventDetailPage` — inclusive o `style={{ fontSize: 13 }}`
 * digitado em cada ícone.
 *
 * `whiteAlpha` em vez das cores do registry (`ActionButton`) porque o fundo é `gray.900`: um
 * botão verde de "baixar" ou azul de "ver" ali dentro seria o único elemento colorido do banner.
 */
export const HERO_BUTTON_PROPS: ButtonProps = {
  size: 'sm',
  variant: 'outline',
  colorScheme: 'whiteAlpha',
  color: 'whiteAlpha.800',
  borderColor: 'whiteAlpha.300',
  fontWeight: 'normal',
  fontSize: 'sm',
  _hover: { bg: 'whiteAlpha.100', color: 'white', textDecoration: 'none' }
}

export interface IHeroButtonProps extends Omit<ButtonProps, 'leftIcon' | 'rightIcon' | 'children'> {
  /** Classe do LineIcons (`lni lni-download`). O tamanho vem do token `md` do `<Icon>`. */
  icon?: string
  /** Navegação interna via react-router. */
  to?: string
  /** Link externo — abre em nova aba com o ícone de "sai do painel". */
  href?: string
  children: ReactNode
}

/** Botão da fileira de ações do `<DetailHero>`. */
export default function HeroButton({ icon, to, href, children, ...rest }: IHeroButtonProps) {
  const linkProps = to
    ? { as: Link, to }
    : href
      ? { as: 'a' as const, href, target: '_blank', rel: 'noopener noreferrer' }
      : {}

  const buttonProps: ButtonProps = {
    ...HERO_BUTTON_PROPS,
    ...linkProps,
    leftIcon: icon ? <Icon name={icon} size="md" /> : undefined,
    // A seta de "sai do painel" só faz sentido no link externo, que abre em outra aba.
    rightIcon: href ? <Icon name="lni lni-exit-up" size="sm" /> : undefined,
    ...rest
  }

  return <Button {...buttonProps}>{children}</Button>
}
